import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, FileText, Search, Filter, 
  Calendar, Users, DollarSign, CheckCircle, XCircle,
  AlertCircle, Eye, Loader2, Plus, RefreshCw, ChevronDown, Edit, Trash2,
  Receipt
} from 'lucide-react';
import CrearCuentaCobroManual from '../components/CrearCuentaCobroManual';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { ActionButton } from '../components/ui/ActionButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CuentasCobro = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filtros
  const [filtroNumero, setFiltroNumero] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroAliado, setFiltroAliado] = useState('');
  
  // Paginación
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20);
  
  // Aliados (para filtro)
  const [aliados, setAliados] = useState([]);
  
  // Modal de detalle
  const [cuentaDetalle, setCuentaDetalle] = useState(null);
  const [showDetalle, setShowDetalle] = useState(false);
  
  // Modal de edición
  const [showEditarCuenta, setShowEditarCuenta] = useState(false);
  const [cuentaAEditar, setCuentaAEditar] = useState(null);
  const [motivoEdicion, setMotivoEdicion] = useState('');
  const [editandoCuenta, setEditandoCuenta] = useState(false);
  
  // Modal de nueva cuenta
  const [showNuevaCuenta, setShowNuevaCuenta] = useState(false);
  const [showCrearManual, setShowCrearManual] = useState(false);
  const [aliadoSeleccionado, setAliadoSeleccionado] = useState('');
  const [referidosDisponibles, setReferidosDisponibles] = useState([]);
  const [referidosSeleccionados, setReferidosSeleccionados] = useState([]);
  const [generandoCuenta, setGenerandoCuenta] = useState(false);

  const cargarCuentas = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filtroNumero) params.append('numero', filtroNumero);
      if (filtroEstado) params.append('estado', filtroEstado);
      if (filtroAliado) params.append('aliado_id', filtroAliado);
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      
      const response = await axios.get(
        `${API_URL}/api/cuentas-cobro?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCuentas(response.data.cuentas_cobro || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar cuentas de cobro');
    } finally {
      setLoading(false);
    }
  }, [filtroNumero, filtroEstado, filtroAliado, skip, limit]);

  const cargarAliados = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/aliados`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAliados(response.data.aliados || []);
    } catch (error) {
      console.error('Error cargando aliados:', error);
    }
  }, []);

  useEffect(() => {
    cargarCuentas();
    if (user?.role === 'admin' || user?.role === 'gerencia') {
      cargarAliados();
    }
  }, [cargarCuentas, cargarAliados, user]);

  const descargarPDF = async (cuentaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/cuentas-cobro/${cuentaId}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuenta_cobro_${cuentaId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const verDetalle = async (cuentaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/cuentas-cobro/${cuentaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCuentaDetalle(response.data);
      setShowDetalle(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const anularCuenta = async (cuentaId) => {
    if (!window.confirm('¿Está seguro de anular esta cuenta de cobro? El número serial NO se reutilizará.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/cuentas-cobro/${cuentaId}/anular`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cuenta anulada correctamente');
      cargarCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular cuenta');
    }
  };

  // Eliminar permanentemente - SOLO GERENCIA
  const eliminarCuentaPermanente = async (cuentaId, numeroCuenta) => {
    const confirmacion = window.confirm(
      `⚠️ ELIMINAR PERMANENTEMENTE\n\n` +
      `¿Está seguro de eliminar la cuenta ${numeroCuenta}?\n\n` +
      `Esta acción es IRREVERSIBLE y eliminará el registro de la base de datos.\n` +
      `Se guardará un registro de auditoría.`
    );
    
    if (!confirmacion) return;
    
    // Segunda confirmación
    const confirmacion2 = window.prompt(
      `Para confirmar, escriba el número de cuenta: ${numeroCuenta}`
    );
    
    if (confirmacion2 !== numeroCuenta) {
      toast.error('El número de cuenta no coincide. Operación cancelada.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/cuentas-cobro/${cuentaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cuenta ${numeroCuenta} eliminada permanentemente`);
      cargarCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cuenta');
    }
  };

  // Aprobar cuenta de cobro - Solo Admin/Gerencia
  const aprobarCuenta = async (cuentaId) => {
    if (!window.confirm('¿Está seguro de aprobar esta cuenta de cobro? Se cambiará a estado "Emitida".')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/cuentas-cobro/${cuentaId}/aprobar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cuenta de cobro aprobada y emitida correctamente');
      cargarCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar cuenta');
    }
  };

  // Rechazar cuenta de cobro - Solo Admin/Gerencia
  const rechazarCuenta = async (cuentaId) => {
    const motivo = window.prompt('Ingrese el motivo del rechazo:');
    if (motivo === null) return; // Usuario canceló
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/cuentas-cobro/${cuentaId}/rechazar`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { motivo: motivo || 'Sin motivo especificado' }
        }
      );
      toast.success('Cuenta de cobro rechazada');
      cargarCuentas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar cuenta');
    }
  };

  const cargarReferidosDisponibles = async (aliadoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/aliados/${aliadoId}/referidos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar solo referidos con comisión pendiente y que no estén en otra cuenta
      const disponibles = (response.data.referidos || []).filter(r => 
        r.estado_comision === 'calculada' && 
        !r.id_cuenta_cobro &&
        r.valor_comision > 0
      );
      
      setReferidosDisponibles(disponibles);
      setReferidosSeleccionados(disponibles.map(r => r.id));
    } catch (error) {
      toast.error('Error al cargar referidos');
    }
  };

  const generarNuevaCuenta = async () => {
    if (!aliadoSeleccionado) {
      toast.error('Seleccione un aliado');
      return;
    }
    if (referidosSeleccionados.length === 0) {
      toast.error('Seleccione al menos un referido');
      return;
    }
    
    setGenerandoCuenta(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/aliados/${aliadoSeleccionado}/cuenta-cobro/generar`,
        referidosSeleccionados,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Descargar PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuenta_cobro_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Cuenta de cobro generada y descargada');
      setShowNuevaCuenta(false);
      setAliadoSeleccionado('');
      setReferidosDisponibles([]);
      setReferidosSeleccionados([]);
      cargarCuentas();
    } catch (error) {
      let errorMsg = 'Error al generar cuenta';
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch {}
      } else {
        errorMsg = error.response?.data?.detail || errorMsg;
      }
      toast.error(errorMsg);
    } finally {
      setGenerandoCuenta(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'borrador': return 'bg-zinc-600 text-zinc-200 border-zinc-500';
      case 'pendiente_aprobacion': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'emitida': return 'bg-green-100 text-green-700 border-green-200';
      case 'emitida_con_excepcion': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'pagada': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'anulada': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-zinc-100 text-zinc-700 border-zinc-200';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'borrador': return 'Borrador';
      case 'pendiente_aprobacion': return 'Pendiente';
      case 'emitida': return 'Emitida';
      case 'emitida_con_excepcion': return 'Excepción';
      case 'pagada': return 'Pagada';
      case 'anulada': return 'Anulada';
      default: return estado;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  // ==================== FUNCIONES DE EDICIÓN ====================
  
  // Verificar si una cuenta puede ser editada
  // Reglas: BORRADOR siempre editable, EMITIDA hasta 8 días, PAGADA/ANULADA nunca
  const puedeEditarCuenta = (cuenta) => {
    // Solo Admin y Gerencia
    if (!['admin', 'gerencia'].includes(user?.role)) return false;
    
    // PAGADA o ANULADA: NO editable nunca
    if (['pagada', 'anulada'].includes(cuenta.estado)) return false;
    
    // BORRADOR: siempre editable sin restricción de fecha
    if (cuenta.estado === 'borrador') return true;
    
    // EMITIDA o pendiente_aprobacion: verificar fecha límite (8 días desde emisión)
    const fechaEmision = cuenta.fecha_emision || cuenta.created_at;
    if (!fechaEmision) return true; // Sin fecha, permitir edición
    
    const fechaLimite = new Date(fechaEmision);
    fechaLimite.setDate(fechaLimite.getDate() + 8);
    
    return new Date() <= fechaLimite;
  };
  
  // Determinar si se requiere motivo para editar
  const requiereMotivoEdicion = (cuenta) => {
    // Solo EMITIDA requiere motivo, BORRADOR no
    return cuenta.estado && ['emitida', 'emitida_con_excepcion', 'pendiente_aprobacion'].includes(cuenta.estado);
  };
  
  // Obtener mensaje de por qué no se puede editar
  const getMensajeNoEditable = (cuenta) => {
    if (!['admin', 'gerencia'].includes(user?.role)) {
      return "Solo Gerencia o Administración pueden editar cuentas";
    }
    if (cuenta.estado === 'pagada') {
      return "Esta cuenta ya fue marcada como PAGADA";
    }
    if (cuenta.estado === 'anulada') {
      return "Esta cuenta fue ANULADA";
    }
    // BORRADOR no tiene restricción de fecha
    if (cuenta.estado === 'borrador') {
      return "";
    }
    const fechaEmision = cuenta.fecha_emision || cuenta.created_at;
    if (fechaEmision) {
      const fechaLimite = new Date(fechaEmision);
      fechaLimite.setDate(fechaLimite.getDate() + 8);
      if (new Date() > fechaLimite) {
        return `Fecha límite de edición superada (${fechaLimite.toLocaleDateString('es-CO')})`;
      }
    }
    return "";
  };
  
  // Abrir modal de edición
  const abrirEdicion = async (cuentaId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Verificar si puede editar
      const checkResponse = await axios.get(
        `${API_URL}/api/cuentas-cobro/${cuentaId}/puede-editar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!checkResponse.data.puede_editar) {
        toast.error(checkResponse.data.mensaje);
        return;
      }
      
      // Obtener cuenta completa
      const cuentaResponse = await axios.get(
        `${API_URL}/api/cuentas-cobro/${cuentaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCuentaAEditar(cuentaResponse.data);
      setMotivoEdicion('');
      setShowEditarCuenta(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar la cuenta');
    }
  };
  
  // Guardar edición
  const guardarEdicion = async () => {
    // Motivo obligatorio SOLO para cuentas EMITIDAS
    const necesitaMotivo = requiereMotivoEdicion(cuentaAEditar);
    
    if (necesitaMotivo && !motivoEdicion.trim()) {
      toast.error('Debes ingresar el motivo del cambio para cuentas EMITIDAS');
      return;
    }
    
    if (necesitaMotivo && motivoEdicion.trim().length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    
    setEditandoCuenta(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        motivo_edicion: motivoEdicion.trim() || 'Edición de borrador',
        datos_aliado: cuentaAEditar.datos_aliado,
        datos_bancarios: cuentaAEditar.datos_bancarios,
        datos_cobro: cuentaAEditar.datos_cobro,
        conceptos: cuentaAEditar.conceptos,
        total: cuentaAEditar.total
      };
      
      const response = await axios.put(
        `${API_URL}/api/cuentas-cobro/${cuentaAEditar.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Cuenta editada correctamente. Versión ${response.data.version}`);
      setShowEditarCuenta(false);
      setCuentaAEditar(null);
      setMotivoEdicion('');
      cargarCuentas();
    } catch (error) {
      let errorMsg = 'Error al guardar cambios';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => e.msg || String(e)).join(', ');
        } else {
          errorMsg = error.response.data.detail;
        }
      }
      toast.error(errorMsg);
    } finally {
      setEditandoCuenta(false);
    }
  };
  
  // Actualizar campo de la cuenta a editar
  const actualizarCampoEdicion = (seccion, campo, valor) => {
    setCuentaAEditar(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        [campo]: valor
      }
    }));
  };
  
  // Actualizar concepto de la cuenta a editar
  const actualizarConceptoEdicion = (index, campo, valor) => {
    setCuentaAEditar(prev => {
      const nuevosConceptos = [...(prev.conceptos || [])];
      nuevosConceptos[index] = {
        ...nuevosConceptos[index],
        [campo]: valor
      };
      
      // Recalcular total si cambia valor_pagar
      if (campo === 'valor_pagar' || campo === 'valor_final') {
        const nuevoTotal = nuevosConceptos.reduce((sum, c) => {
          return sum + (c.valor_pagar || c.valor_final || c.valor_comision || 0);
        }, 0);
        return { ...prev, conceptos: nuevosConceptos, total: nuevoTotal };
      }
      
      return { ...prev, conceptos: nuevosConceptos };
    });
  };

  // Agregar nuevo concepto en edición
  const agregarConceptoEdicion = () => {
    setCuentaAEditar(prev => {
      const nuevosConceptos = [...(prev.conceptos || [])];
      nuevosConceptos.push({
        tipo_concepto: 'servicio',
        concepto_cobro: '',
        cliente_tercero: '',
        tipo_referido: null,
        valor_con_iva: 0,
        valor_base_sin_iva: 0,
        iva_incluido: 0,
        porcentaje_comision: 0,
        valor_comision: 0,
        valor_pagar: 0,
        valor_final: 0,
        observaciones: '',
        referido_id: null
      });
      return { ...prev, conceptos: nuevosConceptos };
    });
    toast.success('Concepto agregado');
  };

  // Eliminar concepto en edición
  const eliminarConceptoEdicion = (index) => {
    setCuentaAEditar(prev => {
      const nuevosConceptos = [...(prev.conceptos || [])];
      if (nuevosConceptos.length <= 1) {
        toast.error('Debe haber al menos un concepto');
        return prev;
      }
      nuevosConceptos.splice(index, 1);
      // Recalcular total
      const nuevoTotal = nuevosConceptos.reduce((sum, c) => {
        return sum + (c.valor_pagar || c.valor_final || c.valor_comision || 0);
      }, 0);
      return { ...prev, conceptos: nuevosConceptos, total: nuevoTotal };
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header Fijo con Safe Area para iOS */}
      <header className="fixed top-0 left-0 w-full z-50 bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800 shadow-lg safe-area-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          {/* Mobile: 2 rows */}
          <div className="flex flex-col gap-3 md:hidden">
            {/* Row 1: Back + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-serif truncate">Cuentas de Cobro</h1>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{total} registradas</p>
                </div>
              </div>
            </div>
            {/* Row 2: Actions (only for admin/gerencia/aliado) */}
            {(user?.role === 'admin' || user?.role === 'gerencia') && (
              <div className="flex gap-2">
                <ActionButton
                  onClick={() => setShowNuevaCuenta(true)}
                  variant="success"
                  icon={Plus}
                  className="flex-1 justify-center text-xs"
                  data-testid="generar-cuenta-btn-mobile"
                >
                  Desde Referidos
                </ActionButton>
                <ActionButton
                  onClick={() => setShowCrearManual(true)}
                  variant="warning"
                  icon={Edit}
                  className="flex-1 justify-center text-xs"
                  data-testid="crear-manual-btn-mobile"
                >
                  Manual
                </ActionButton>
              </div>
            )}
            {user?.role === 'aliado' && (
              <ActionButton
                onClick={() => setShowCrearManual(true)}
                variant="gold"
                icon={Plus}
                className="w-full justify-center"
                data-testid="crear-cuenta-aliado-mobile"
              >
                Crear Cuenta
              </ActionButton>
            )}
          </div>
          
          {/* Desktop: Single row */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-xl font-serif">Cuentas de Cobro</h1>
                  <p className="text-xs text-zinc-400 uppercase tracking-wider">{total} {total === 1 ? 'cuenta registrada' : 'cuentas registradas'}</p>
                </div>
              </div>
            </div>
          
            {/* Botones para Admin/Gerencia */}
            {(user?.role === 'admin' || user?.role === 'gerencia') && (
              <div className="flex gap-2">
                <ActionButton
                  onClick={() => setShowNuevaCuenta(true)}
                  variant="success"
                  icon={Plus}
                  data-testid="generar-cuenta-btn"
                >
                  Generar desde Referidos
                </ActionButton>
                <ActionButton
                  onClick={() => setShowCrearManual(true)}
                  variant="warning"
                  icon={Edit}
                  data-testid="crear-manual-btn"
                >
                  Crear Manual
                </ActionButton>
              </div>
            )}
          
          {/* Botón para Aliados Proveedores/Prestadores */}
          {user?.role === 'aliado' && (
            <ActionButton
              onClick={() => setShowCrearManual(true)}
              variant="warning"
              icon={Plus}
              data-testid="crear-mi-cuenta-btn"
            >
              Crear Mi Cuenta de Cobro
            </ActionButton>
          )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 content-below-safe-header">
        {/* Filtros */}
        <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-emerald-500 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-medium">Buscar por número</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  placeholder="JPM-CC-000001"
                  value={filtroNumero}
                  onChange={(e) => setFiltroNumero(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                  data-testid="filtro-numero"
                />
              </div>
            </div>
            
            <div className="min-w-[150px]">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-medium">Estado</label>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                data-testid="filtro-estado"
              >
                <option value="">Todos</option>
                <option value="borrador">Borrador</option>
                <option value="pendiente_aprobacion">Pendiente Aprobación</option>
                <option value="emitida">Emitida</option>
                <option value="emitida_con_excepcion">Con Excepción</option>
                <option value="pagada">Pagada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
            
            {(user?.role === 'admin' || user?.role === 'gerencia') && (
              <div className="min-w-[200px]">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-medium">Aliado</label>
                <select
                  value={filtroAliado}
                  onChange={(e) => setFiltroAliado(e.target.value)}
                  className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                  data-testid="filtro-aliado"
                >
                  <option value="">Todos los aliados</option>
                  {aliados.map(a => (
                    <option key={a.id} value={a.id}>{a.nombre}</option>
                  ))}
                </select>
              </div>
            )}
            
            <button
              onClick={cargarCuentas}
              className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-4 py-2.5 rounded-xl transition-colors border border-zinc-700"
              data-testid="actualizar-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Resumen en tarjetas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-emerald-500 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-xl">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-serif">{total}</p>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Total Cuentas</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-green-500 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-500/20 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-serif">
                  {cuentas.filter(c => c.estado === 'emitida' || c.estado === 'emitida_con_excepcion').length}
                </p>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Emitidas</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-blue-500 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500/20 rounded-xl">
                <DollarSign className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-serif">
                  {cuentas.filter(c => c.estado === 'pagada').length}
                </p>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Pagadas</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-amber-500 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-500/20 rounded-xl">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-lg font-serif">
                  {formatCurrency(cuentas.reduce((sum, c) => sum + (c.total || 0), 0))}
                </p>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Total Comisiones</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de cuentas */}
        <div className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-emerald-500 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : cuentas.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No hay cuentas de cobro"
              description="Aún no se han generado cuentas de cobro. Puedes generar una desde los referidos aprobados o crear una manualmente."
              actionLabel="Generar Cuenta"
              onAction={() => setShowNuevaCuenta(true)}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">No. Serial</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Aliado</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Fecha</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-zinc-400">Total</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-zinc-400">Referidos</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-zinc-400">Estado</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-zinc-400">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {cuentas.map(cuenta => (
                    <tr key={cuenta.id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-emerald-400 font-medium">
                          {cuenta.numero}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {cuenta.aliado_nombre && cuenta.aliado_nombre !== 'Desconocido' && cuenta.id_aliado ? (
                          <span className="text-white">{cuenta.aliado_nombre}</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-amber-400 font-medium">Sin aliado vinculado</span>
                            <AlertCircle className="w-4 h-4 text-amber-500" title="Esta cuenta debe vincularse a un aliado existente" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-400">
                        {new Date(cuenta.created_at).toLocaleDateString('es-CO')}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-white">
                        {formatCurrency(cuenta.total)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-zinc-400">
                          <Users className="w-4 h-4" />
                          {cuenta.referidos_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getEstadoColor(cuenta.estado)}`}>
                          {getEstadoLabel(cuenta.estado)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => verDetalle(cuenta.id)}
                            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                            title="Ver detalle"
                          >
                            <Eye className="w-4 h-4 text-zinc-400" />
                          </button>
                          <button
                            onClick={() => descargarPDF(cuenta.id)}
                            className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                            title="Descargar PDF"
                          >
                            <Download className="w-4 h-4 text-emerald-500" />
                          </button>
                          {puedeEditarCuenta(cuenta) && (
                            <button
                              onClick={() => abrirEdicion(cuenta.id)}
                              className="p-2 hover:bg-amber-900/30 rounded-lg transition-colors"
                              title="Editar cuenta"
                              data-testid={`editar-cuenta-${cuenta.id}`}
                            >
                              <Edit className="w-4 h-4 text-amber-500" />
                            </button>
                          )}
                          {/* Botón anular - Admin y Gerencia */}
                          {(user?.role === 'admin' || user?.role === 'gerencia') && cuenta.estado !== 'anulada' && (
                            <button
                              onClick={() => anularCuenta(cuenta.id)}
                              className="p-2 hover:bg-orange-900/30 rounded-lg transition-colors"
                              title="Anular cuenta"
                              data-testid={`anular-cuenta-${cuenta.id}`}
                            >
                              <XCircle className="w-4 h-4 text-orange-500" />
                            </button>
                          )}
                          {/* Botones Aprobar/Rechazar para cuentas pendientes - Solo Admin/Gerencia */}
                          {(user?.role === 'admin' || user?.role === 'gerencia') && cuenta.estado === 'pendiente_aprobacion' && (
                            <>
                              <button
                                onClick={() => aprobarCuenta(cuenta.id)}
                                className="p-2 hover:bg-emerald-900/30 rounded-lg transition-colors"
                                title="Aprobar cuenta"
                                data-testid={`aprobar-cuenta-${cuenta.id}`}
                              >
                                <CheckCircle className="w-4 h-4 text-emerald-500" />
                              </button>
                              <button
                                onClick={() => rechazarCuenta(cuenta.id)}
                                className="p-2 hover:bg-red-900/30 rounded-lg transition-colors"
                                title="Rechazar cuenta"
                                data-testid={`rechazar-cuenta-${cuenta.id}`}
                              >
                                <XCircle className="w-4 h-4 text-red-500" />
                              </button>
                            </>
                          )}
                          {/* Botón eliminar permanentemente - SOLO GERENCIA */}
                          {user?.role === 'gerencia' && (
                            <button
                              onClick={() => eliminarCuentaPermanente(cuenta.id, cuenta.numero)}
                              className="p-2 hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Eliminar permanentemente"
                              data-testid={`eliminar-cuenta-${cuenta.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Paginación */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <p className="text-sm text-zinc-400">
                Mostrando {skip + 1} - {Math.min(skip + limit, total)} de {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                  className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= total}
                  className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal Detalle */}
      {showDetalle && cuentaDetalle && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Detalle de Cuenta de Cobro</h2>
                <p className="text-emerald-400 font-mono">{cuentaDetalle.numero}</p>
              </div>
              <button
                onClick={() => setShowDetalle(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Info general */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-400">Aliado</p>
                  <p className="font-medium">{cuentaDetalle.aliado?.nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Fecha de generación</p>
                  <p className="font-medium">
                    {new Date(cuentaDetalle.created_at).toLocaleString('es-CO')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Estado</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getEstadoColor(cuentaDetalle.estado)}`}>
                    {getEstadoLabel(cuentaDetalle.estado)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Total</p>
                  <p className="text-xl font-bold text-emerald-400">
                    {formatCurrency(cuentaDetalle.total)}
                  </p>
                </div>
              </div>
              
              {/* Si es con excepción */}
              {cuentaDetalle.emitida_con_excepcion && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-400">Emitida con Excepción</p>
                      <p className="text-sm text-zinc-300 mt-1">
                        <strong>Autorizado por:</strong> {cuentaDetalle.autorizacion_excepcional?.usuario_nombre || cuentaDetalle.autorizacion_excepcional?.usuario_email}
                      </p>
                      <p className="text-sm text-zinc-300">
                        <strong>Motivo:</strong> {cuentaDetalle.autorizacion_excepcional?.motivo}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Referidos incluidos */}
              <div>
                <h3 className="font-medium mb-3">Referidos Incluidos ({cuentaDetalle.referidos_detalle?.length || 0})</h3>
                <div className="space-y-2">
                  {cuentaDetalle.referidos_detalle?.map(ref => (
                    <div key={ref.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                      <div>
                        <p className="font-medium">{ref.nombre}</p>
                        <p className="text-sm text-zinc-400">
                          {ref.tipo_referido} • {ref.porcentaje_comision}% comisión
                        </p>
                      </div>
                      <p className="text-emerald-400 font-medium">
                        {formatCurrency(ref.valor_comision)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => setShowDetalle(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cerrar
              </button>
              <button
                onClick={() => descargarPDF(cuentaDetalle.id)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg"
              >
                <Download className="w-4 h-4" />
                Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nueva Cuenta */}
      {showNuevaCuenta && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <h2 className="text-xl font-semibold">Generar Nueva Cuenta de Cobro</h2>
              <p className="text-sm text-zinc-400">
                Se asignará automáticamente un número serial consecutivo
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Seleccionar aliado */}
              <div>
                <label className="block text-sm font-medium mb-2">Seleccionar Aliado</label>
                <select
                  value={aliadoSeleccionado}
                  onChange={(e) => {
                    setAliadoSeleccionado(e.target.value);
                    if (e.target.value) {
                      cargarReferidosDisponibles(e.target.value);
                    } else {
                      setReferidosDisponibles([]);
                      setReferidosSeleccionados([]);
                    }
                  }}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">-- Seleccionar aliado --</option>
                  {aliados.map(a => (
                    <option key={a.id} value={a.id}>{a.nombre}</option>
                  ))}
                </select>
              </div>
              
              {/* Referidos disponibles */}
              {aliadoSeleccionado && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Referidos Disponibles ({referidosDisponibles.length})
                  </label>
                  
                  {referidosDisponibles.length === 0 ? (
                    <div className="bg-zinc-800 rounded-lg p-4 text-center">
                      <AlertCircle className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                      <p className="text-zinc-400">No hay referidos con comisiones pendientes para este aliado</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {referidosDisponibles.map(ref => (
                        <label
                          key={ref.id}
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-700"
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={referidosSeleccionados.includes(ref.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setReferidosSeleccionados([...referidosSeleccionados, ref.id]);
                                } else {
                                  setReferidosSeleccionados(referidosSeleccionados.filter(id => id !== ref.id));
                                }
                              }}
                              className="w-4 h-4 rounded border-zinc-600 text-emerald-500 focus:ring-emerald-500"
                            />
                            <div>
                              <p className="font-medium">{ref.nombre}</p>
                              <p className="text-sm text-zinc-400">
                                {ref.tipo_referido} • {ref.porcentaje_comision}%
                              </p>
                            </div>
                          </div>
                          <p className="text-emerald-400 font-medium">
                            {formatCurrency(ref.valor_comision)}
                          </p>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {referidosSeleccionados.length > 0 && (
                    <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <p className="text-sm text-emerald-400">
                        <strong>Total a generar:</strong> {formatCurrency(
                          referidosDisponibles
                            .filter(r => referidosSeleccionados.includes(r.id))
                            .reduce((sum, r) => sum + (r.valor_comision || 0), 0)
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowNuevaCuenta(false);
                  setAliadoSeleccionado('');
                  setReferidosDisponibles([]);
                  setReferidosSeleccionados([]);
                }}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={generarNuevaCuenta}
                disabled={generandoCuenta || referidosSeleccionados.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded-lg"
              >
                {generandoCuenta ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Generar Cuenta de Cobro
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Cuenta Manual */}
      {showCrearManual && (
        <CrearCuentaCobroManual
          onClose={() => setShowCrearManual(false)}
          onSuccess={() => {
            setShowCrearManual(false);
            cargarCuentas();
          }}
        />
      )}

      {/* Modal Editar Cuenta de Cobro */}
      {showEditarCuenta && cuentaAEditar && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header del modal */}
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Edit className="w-5 h-5 text-amber-500" />
                  Editar Cuenta de Cobro
                </h2>
                <p className="text-amber-400 font-mono">{cuentaAEditar.numero}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Versión actual: {cuentaAEditar.version || 1}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEditarCuenta(false);
                  setCuentaAEditar(null);
                  setMotivoEdicion('');
                }}
                className="p-2 hover:bg-zinc-800 rounded-lg"
                data-testid="cerrar-modal-edicion"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Alerta sobre edición */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-400">Importante sobre la edición</p>
                    <ul className="text-sm text-zinc-300 mt-1 list-disc list-inside space-y-1">
                      <li>El número de cuenta <strong>{cuentaAEditar.numero}</strong> NO cambiará</li>
                      <li>La fecha de emisión original se mantiene</li>
                      <li>Se registrará quién editó, cuándo y el motivo</li>
                      <li>El PDF se regenerará con una nota de edición</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Sección: Datos del Aliado/Beneficiario */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-500" />
                  Datos del Aliado / Beneficiario
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Nombre del Aliado</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_aliado?.nombre || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_aliado', 'nombre', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-nombre-aliado"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Documento (CC/NIT)</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_aliado?.numero_documento || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_aliado', 'numero_documento', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-documento-aliado"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Teléfono</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_aliado?.telefono || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_aliado', 'telefono', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-telefono-aliado"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Correo</label>
                    <input
                      type="email"
                      value={cuentaAEditar.datos_aliado?.correo || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_aliado', 'correo', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-correo-aliado"
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Datos Bancarios */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  Datos Bancarios para Pago
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Titular de la Cuenta</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_bancarios?.titular_cuenta || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_bancarios', 'titular_cuenta', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-titular-cuenta"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Documento del Titular</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_bancarios?.documento_titular || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_bancarios', 'documento_titular', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-documento-titular"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Banco</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_bancarios?.banco || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_bancarios', 'banco', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-banco"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Tipo de Cuenta</label>
                    <select
                      value={cuentaAEditar.datos_bancarios?.tipo_cuenta || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_bancarios', 'tipo_cuenta', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-tipo-cuenta"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="ahorros">Ahorros</option>
                      <option value="corriente">Corriente</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Número de Cuenta</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_bancarios?.numero_cuenta || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_bancarios', 'numero_cuenta', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-numero-cuenta"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Llave de Pago (Nequi/Daviplata)</label>
                    <input
                      type="text"
                      value={cuentaAEditar.datos_bancarios?.llave_pago || ''}
                      onChange={(e) => actualizarCampoEdicion('datos_bancarios', 'llave_pago', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                      data-testid="edicion-llave-pago"
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Conceptos/Items */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-500" />
                    Conceptos / Items de la Cuenta
                  </h3>
                  {/* Botón Agregar Concepto - SIEMPRE visible excepto en PAGADA */}
                  {cuentaAEditar.estado !== 'pagada' && (
                    <button
                      type="button"
                      onClick={agregarConceptoEdicion}
                      className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-medium transition-colors"
                      data-testid="agregar-concepto-edicion"
                    >
                      <Plus className="w-4 h-4" />
                      Agregar concepto
                    </button>
                  )}
                </div>
                
                {cuentaAEditar.conceptos && cuentaAEditar.conceptos.length > 0 ? (
                  <div className="space-y-4">
                    {cuentaAEditar.conceptos.map((concepto, index) => (
                      <div key={concepto.id || concepto.referido_id || `concepto-${index}-${concepto.descripcion?.slice(0,10)}`} className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-zinc-400">
                            Item #{index + 1}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-zinc-700 px-2 py-1 rounded">
                              {concepto.tipo_concepto || concepto.tipo_referido || 'concepto'}
                            </span>
                            {/* Botón eliminar concepto */}
                            {cuentaAEditar.estado !== 'pagada' && cuentaAEditar.conceptos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => eliminarConceptoEdicion(index)}
                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
                                title="Eliminar concepto"
                                data-testid={`eliminar-concepto-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm text-zinc-400 mb-1">Concepto</label>
                            <input
                              type="text"
                              value={concepto.concepto_cobro || concepto.concepto || ''}
                              onChange={(e) => actualizarConceptoEdicion(index, 'concepto_cobro', e.target.value)}
                              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                              data-testid={`edicion-concepto-${index}`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-zinc-400 mb-1">Valor a Pagar</label>
                            <input
                              type="number"
                              value={concepto.valor_pagar || concepto.valor_final || concepto.valor_comision || 0}
                              onChange={(e) => actualizarConceptoEdicion(index, 'valor_pagar', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                              data-testid={`edicion-valor-${index}`}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm text-zinc-400 mb-1">Cliente / Tercero</label>
                            <input
                              type="text"
                              value={concepto.cliente_tercero || concepto.nombre_cliente || concepto.nombre || ''}
                              onChange={(e) => actualizarConceptoEdicion(index, 'cliente_tercero', e.target.value)}
                              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-emerald-500 focus:outline-none"
                              data-testid={`edicion-cliente-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Total calculado */}
                    <div className="flex justify-end">
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-6 py-3">
                        <p className="text-sm text-zinc-400">Total de la cuenta</p>
                        <p className="text-2xl font-bold text-emerald-400">
                          {formatCurrency(cuentaAEditar.total || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-zinc-500 mb-4">No hay conceptos</p>
                    <button
                      type="button"
                      onClick={agregarConceptoEdicion}
                      className="flex items-center gap-2 mx-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm"
                      data-testid="agregar-primer-concepto"
                    >
                      <Plus className="w-4 h-4" />
                      Agregar primer concepto
                    </button>
                  </div>
                )}
              </div>

              {/* Sección: Motivo de Edición - CONDICIONAL según estado */}
              {requiereMotivoEdicion(cuentaAEditar) ? (
                <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-2 flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    Motivo de la Edición *
                  </h3>
                  <p className="text-sm text-zinc-400 mb-3">
                    Este campo es obligatorio para cuentas EMITIDAS y quedará registrado en el historial.
                  </p>
                  <textarea
                    value={motivoEdicion}
                    onChange={(e) => setMotivoEdicion(e.target.value)}
                    placeholder="Describa el motivo de la edición (mínimo 10 caracteres)..."
                    rows={3}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-red-500 focus:outline-none resize-none"
                    data-testid="edicion-motivo"
                  />
                  <p className={`text-xs mt-1 ${motivoEdicion.length >= 10 ? 'text-green-500' : 'text-red-400'}`}>
                    {motivoEdicion.length}/10 caracteres mínimo
                  </p>
                </div>
              ) : (
                <div className="bg-blue-500/5 border border-blue-500/30 rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-2 flex items-center gap-2 text-blue-400">
                    <FileText className="w-5 h-5" />
                    Notas de Edición (Opcional)
                  </h3>
                  <p className="text-sm text-zinc-400 mb-3">
                    Puedes agregar notas sobre esta edición del borrador.
                  </p>
                  <textarea
                    value={motivoEdicion}
                    onChange={(e) => setMotivoEdicion(e.target.value)}
                    placeholder="Notas opcionales sobre la edición..."
                    rows={2}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
                    data-testid="edicion-notas"
                  />
                </div>
              )}
            </div>
            
            {/* Footer del modal */}
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3 sticky bottom-0 bg-zinc-900">
              <button
                onClick={() => {
                  setShowEditarCuenta(false);
                  setCuentaAEditar(null);
                  setMotivoEdicion('');
                }}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="cancelar-edicion"
              >
                Cancelar
              </button>
              <button
                onClick={guardarEdicion}
                disabled={editandoCuenta || (requiereMotivoEdicion(cuentaAEditar) && motivoEdicion.trim().length < 10)}
                className="flex items-center gap-2 px-6 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded-lg transition-colors"
                data-testid="guardar-edicion"
              >
                {editandoCuenta ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {cuentaAEditar.estado === 'borrador' ? 'Guardar Borrador' : 'Guardar Cambios'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasCobro;
